// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Agents browser (design/agents-browser, A; konvo_api agents/AgentsBrowser.ts). A page
// shows in the Konvo desktop app's own browser engine while its traffic goes through Konvo and the
// machine's bridge and out from that machine. This store holds, per machine, the tunnel the desktop
// app keeps (window.electron.agentsBrowser): connecting with a fresh one-use token from Konvo,
// connecting again when it drops, what the machine refused, and its running dev servers. The plain
// web and phones can't route a page through a machine yet; the panel says so.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {agentErrorCode} from '@app/features/konvo_agents/state/AgentsModel';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import type {AgentsBrowserApi, AgentsBrowserEvent} from '@app/types/electron.d';
import type {
	AgentBrowserLimit,
	AgentBrowserLogEntry,
	AgentBrowserSession,
	AgentDevServer,
	AgentDevServers,
} from '@konvo/schema/src/domains/konvo/AgentBrowser';
import type {AgentMachineView} from '@konvo/schema/src/domains/konvo/Agents';
import {makeAutoObservable, observable, runInAction} from 'mobx';
import {targetKey} from '../utils/AgentBrowserUrls';

/** Where the browser can run: the desktop app, an older desktop app, the plain web, a phone. */
export type BrowserSupport = 'desktop' | 'old-app' | 'web' | 'phone';

export type TunnelStatus = 'idle' | 'connecting' | 'ready' | 'lost' | 'failed';

export interface Refusal {
	host: string;
	port: number;
	code: string;
	message?: string;
	reach?: string;
	at: number;
}

export interface MachineTunnel {
	status: TunnelStatus;
	/** The <webview> partition the desktop app set up for this machine. */
	partition: string | null;
	limit: AgentBrowserLimit | null;
	/** Why it could not start (MACHINE_OFFLINE, BRIDGE_TOO_OLD, …). */
	error: {code: string; message: string} | null;
	/** What the machine (or Konvo) refused lately, by host:port; cleared when that address opens. */
	refusals: Map<string, Refusal>;
	attempts: number;
	/** Bumps each time the tunnel is connected again (the page reloads into it). */
	generation: number;
	download: {filename: string; state: string} | null;
}

export interface MachineServers {
	list: Array<AgentDevServer>;
	source: AgentDevServers['source'] | null;
	loading: boolean;
	failed: boolean;
	at: number;
}

const WIDTH_KEY = 'konvo:agents:browser-width';
/** Close codes from the relay (konvo_api agents/AgentsBrowserRelayCore.ts RELAY_CLOSE). */
const LIMIT_CHANGED = 4013;
/** The session reached its longest life (12 h): a fresh one, at once. */
const EXPIRED = 4014;
/** The person may no longer browse there (Agents off, read-only machine, browser off). */
const NO_ACCESS = 4016;
const MAX_ATTEMPTS = 3;

/** Whether the machine's bridge carries the browser (konvo-agents with can.browser). */
export const canBrowse = (machine: AgentMachineView) =>
	machine.limits.send && machine.limits.browserCap !== 'off' && machine.tools.some((tool) => tool.can.browser === true);

/**
 * The desktop app's browser engine (window.electron.agentsBrowser). The sandbox's e2e stands one in
 * as window.__konvoAgentsBrowserStandIn, since Playwright's Chromium has no <webview>; a page can only
 * fool itself with it.
 */
export function browserApi(): AgentsBrowserApi | null {
	return (
		getElectronAPI()?.agentsBrowser ??
		(window as {__konvoAgentsBrowserStandIn?: AgentsBrowserApi}).__konvoAgentsBrowserStandIn ??
		null
	);
}

export function browserSupport(phone: boolean): BrowserSupport {
	const electron = getElectronAPI();
	if (!electron) return !phone && browserApi() ? 'desktop' : phone ? 'phone' : 'web';
	return electron.agentsBrowser ? 'desktop' : 'old-app';
}

/** The relay's view socket, next to the API (…/api/v1 + path, as wss). */
export function viewSocketUrl(
	path: string,
	apiEndpoint = RuntimeConfig.apiEndpoint,
	version = RuntimeConfig.apiCodeVersion,
) {
	const base = new URL(apiEndpoint || '/api', window.location.href);
	const url = new URL(`${base.pathname.replace(/\/$/, '')}/v${version}${path}`, base);
	url.protocol = url.protocol === 'http:' ? 'ws:' : 'wss:';
	return url.toString();
}

function readWidth(): 'phone' | 'desktop' {
	try {
		return getProtectedLocalStorage()?.getItem(WIDTH_KEY) === 'desktop' ? 'desktop' : 'phone';
	} catch {
		return 'phone';
	}
}

function emptyTunnel(): MachineTunnel {
	return {
		status: 'idle',
		partition: null,
		limit: null,
		error: null,
		refusals: observable.map(),
		attempts: 0,
		generation: 0,
		download: null,
	};
}

export class AgentBrowserStore {
	tunnels = observable.map<string, MachineTunnel>();
	servers = observable.map<string, MachineServers>();
	/** The page each thread's panel had open, so it comes back when the panel opens again. */
	lastUrl = observable.map<string, string>();
	/** Phone width (390, as is) or desktop width (1280, scaled to fit). */
	width: 'phone' | 'desktop' = readWidth();
	/** Panels open per machine: the tunnel stays up while one is. */
	private users = new Map<string, number>();
	private unsubscribe: (() => void) | null = null;
	private timers = new Map<string, ReturnType<typeof setTimeout>>();

	constructor() {
		makeAutoObservable<this, 'users' | 'unsubscribe' | 'timers'>(this, {
			users: false,
			unsubscribe: false,
			timers: false,
		});
	}

	tunnel(machineId: string): MachineTunnel {
		return this.tunnels.get(machineId) ?? emptyTunnel();
	}

	refusal(machineId: string, target: {host: string; port: number} | null): Refusal | null {
		if (!target) return null;
		return this.tunnels.get(machineId)?.refusals.get(targetKey(target)) ?? null;
	}

	clearRefusal(machineId: string, target: {host: string; port: number} | null) {
		if (target) this.tunnels.get(machineId)?.refusals.delete(targetKey(target));
	}

	setWidth(width: 'phone' | 'desktop') {
		this.width = width;
		try {
			getProtectedLocalStorage()?.setItem(WIDTH_KEY, width);
		} catch {
			// Kept for this visit only.
		}
	}

	rememberUrl(threadKey: string, url: string) {
		this.lastUrl.set(threadKey, url);
	}

	private listen(api: AgentsBrowserApi) {
		this.unsubscribe ??= api.onEvent((machineId, event) => this.onEvent(machineId, event));
	}

	/** A panel opened: connect the machine's tunnel unless it is up or on its way. */
	async open(machineId: string): Promise<void> {
		this.users.set(machineId, (this.users.get(machineId) ?? 0) + 1);
		const current = this.tunnel(machineId).status;
		if (current === 'ready' || current === 'connecting') return;
		await this.connect(machineId, false);
	}

	/** The last panel on that machine closed: the tunnel goes (the machine's bridge stops dialing in). */
	close(machineId: string) {
		const left = Math.max(0, (this.users.get(machineId) ?? 1) - 1);
		this.users.set(machineId, left);
		if (left > 0) return;
		clearTimeout(this.timers.get(machineId));
		this.timers.delete(machineId);
		void browserApi()?.disconnect(machineId);
		const tunnel = this.tunnels.get(machineId);
		if (tunnel) {
			tunnel.status = 'idle';
			tunnel.attempts = 0;
		}
	}

	/** Try again (the panel's button), from scratch. */
	retry(machineId: string) {
		const tunnel = this.tunnels.get(machineId);
		if (tunnel) tunnel.attempts = 0;
		void this.connect(machineId, false);
	}

	private async connect(machineId: string, again: boolean): Promise<void> {
		const api = browserApi();
		if (!api) return;
		this.listen(api);
		let tunnel = this.tunnels.get(machineId);
		if (!tunnel) {
			this.tunnels.set(machineId, emptyTunnel());
			tunnel = this.tunnels.get(machineId)!;
		}
		tunnel.status = 'connecting';
		tunnel.error = null;
		if (again) tunnel.attempts += 1;
		try {
			const response = await http.post<AgentBrowserSession>(Endpoints.KONVO_AGENTS_BROWSER_SESSION(machineId), {
				body: {},
			});
			const session = response.body;
			const connected = await api.connect({machineId, url: viewSocketUrl(session.path), token: session.token});
			runInAction(() => {
				if (!connected) {
					tunnel.status = 'failed';
					tunnel.error = {code: 'DESKTOP_REFUSED', message: ''};
					return;
				}
				tunnel.partition = connected.partition;
				tunnel.limit = session.limit;
			});
		} catch (error) {
			runInAction(() => {
				tunnel.status = 'failed';
				const body = (error as {body?: {message?: unknown}} | null)?.body;
				tunnel.error = {
					code: agentErrorCode(error) ?? 'ERROR',
					message: typeof body?.message === 'string' ? body.message : '',
				};
			});
		}
	}

	private onEvent(machineId: string, event: AgentsBrowserEvent) {
		const tunnel = this.tunnels.get(machineId);
		if (!tunnel) return;
		switch (event.type) {
			case 'state':
				if (event.state === 'ready') {
					tunnel.status = 'ready';
					tunnel.attempts = 0;
					tunnel.generation += 1;
					if (event.limit === 'machine' || event.limit === 'lan' || event.limit === 'all') tunnel.limit = event.limit;
				} else if (event.state === 'closed') this.closed(machineId, tunnel, event.code);
				return;
			case 'proxy':
				if (event.kind === 'refused')
					tunnel.refusals.set(targetKey(event), {
						host: event.host,
						port: event.port,
						code: event.code ?? 'ERROR',
						...(event.message ? {message: event.message} : {}),
						...(event.reach ? {reach: event.reach} : {}),
						at: Date.now(),
					});
				else tunnel.refusals.delete(targetKey(event));
				return;
			case 'download':
				tunnel.download = {filename: event.filename, state: event.state};
				return;
			case 'popup':
				this.popup = {machineId, url: event.url, at: Date.now()};
				return;
		}
	}

	/** A page asked for a pop-up: the panel opens it in place. */
	popup: {machineId: string; url: string; at: number} | null = null;

	private closed(machineId: string, tunnel: MachineTunnel, code?: number) {
		if ((this.users.get(machineId) ?? 0) === 0 || tunnel.status === 'idle') return;
		if (code === NO_ACCESS) {
			tunnel.status = 'failed';
			tunnel.error = {code: 'NO_ACCESS', message: ''};
			return;
		}
		// The owner changed what it may reach, or the session got old: a new one, with the limit now.
		if (code === LIMIT_CHANGED || code === EXPIRED) {
			tunnel.status = 'connecting';
			void this.connect(machineId, false);
			return;
		}
		if (tunnel.attempts >= MAX_ATTEMPTS) {
			tunnel.status = 'lost';
			return;
		}
		tunnel.status = 'connecting';
		clearTimeout(this.timers.get(machineId));
		this.timers.set(
			machineId,
			setTimeout(() => void this.connect(machineId, true), 1_500 * (tunnel.attempts + 1)),
		);
	}

	/** Tests: stop listening to the desktop app. */
	dispose(): void {
		this.unsubscribe?.();
		this.unsubscribe = null;
	}

	/** The machine's running dev servers (T3 Code's own scan, or a look at common ports). */
	async loadServers(machineId: string, threadId?: string): Promise<void> {
		const current = this.servers.get(machineId);
		if (current?.loading) return;
		this.servers.set(machineId, {
			list: current?.list ?? [],
			source: current?.source ?? null,
			loading: true,
			failed: false,
			at: current?.at ?? 0,
		});
		try {
			const response = await http.get<AgentDevServers>(Endpoints.KONVO_AGENTS_BROWSER_SERVERS(machineId), {
				...(threadId ? {query: {threadId}} : {}),
				mode: 'silent',
			});
			if (!response.ok) throw new Error(String(response.status));
			runInAction(() =>
				this.servers.set(machineId, {
					list: response.body.servers,
					source: response.body.source,
					loading: false,
					failed: false,
					at: Date.now(),
				}),
			);
		} catch {
			runInAction(() =>
				this.servers.set(machineId, {
					list: current?.list ?? [],
					source: current?.source ?? null,
					loading: false,
					failed: true,
					at: Date.now(),
				}),
			);
		}
	}

	/** Who opened what through the machine, the last 30 days (Settings). */
	async loadLog(machineId: string): Promise<Array<AgentBrowserLogEntry>> {
		const response = await http.get<{entries: Array<AgentBrowserLogEntry>}>(
			Endpoints.KONVO_AGENTS_BROWSER_LOG(machineId),
		);
		return response.body.entries;
	}
}

export default new AgentBrowserStore();
